import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { A11yModule } from '@angular/cdk/a11y';
import { GO_MODAL_DATA } from '@go-modules/services/go-modal/go-modal.tokens';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { Masquerade, masqueradeToken } from 'go-modules/masquerade/masquerade.service';
import { goModal, goModalToken } from 'go-modules/modals/go-modal.factory';
import { groupToken } from 'go-modules/models/group-dep/group.factory';
import { userToken } from 'go-modules/models/user/user.factory';
import type { User as UserFactory } from 'go-modules/models/user/user.factory';
import type { Group as GroupFactory } from 'go-modules/models/group-dep/group.factory';
import { User } from '@go-modules/interfaces/user';
import { Group } from '@go-modules/interfaces/groups/group.interface';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { helpUrlsLookup } from 'go-modules/help-urls/help-urls.constant';
import { MatDialog } from '@angular/material/dialog';
import { NgxGroupService } from '@go-modules/services/group/group.service';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { FormsModule } from '@angular/forms';
import { FormatDateModule } from '@go-modules/pipes/format-date/format-date.module';
import { AsyncPipe, NgClass } from '@angular/common';
import { SortPipeModule } from '@go-modules/pipes/sort/sort.pipe.module';
import { IResult, UAParser } from 'ua-parser-js';
import { Credit, UserService as NgxUserService, RecentLoginHistory } from '@go-modules/services/user/user.service';
import { map, Observable } from 'rxjs';
import { CourseFolder } from '@go-modules/services/course/interfaces/course-folder.interface';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { GroupSettingsPanelService, groupSettingsPanelServiceToken } from 'go-modules/group-settings-panel/group-settings-panel.service';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';

export interface EditUser {
	billing_entity_id: number;
	billing_entity_type: string;
	created_at: string;
	created_by: number;
	deleted_at: string;
	deleted_by: number;
	email: string;
	first_name: string;
	fullname?: string;
	group_id: number;
	language: string;
	last_login_at: string;
	last_name: string;
	modified_at: string;
	role: string;
	role_localized: string;
	user_id: number;
}

@Component({
	selector: 'edit-user-dialog',
	templateUrl: './edit-user-dialog.component.html',
	styleUrls: ['./edit-user-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		TranslateModule,
		A11yModule,
		FormsModule,
		FormatDateModule,
		NgClass,
		SortPipeModule,
		AsyncPipe
	]
})
export class EditUserDialogComponent implements OnInit {
	public roles: {
		role: string;
		roleKey: string;
	}[];
	public adminToolsUrl = clientSettings.AdminToolsUrl;
	public helpUrls = helpUrlsLookup;
	public groups$: Observable<CourseFolder[]>;
	public credits$: Observable<Credit[]>;
	public recentLogins$: Observable<RecentLoginHistory[]>;
	public linkedUsers$: Observable<User[]>;
	public ssoProviders$: Observable<string>;
	public role: string;
	public fullname: string;

	constructor (
		public dialogRef: GoDialogRef,
		@Inject(GO_MODAL_DATA) public data: {
			user: EditUser | User;
			group?: Group;
		},
		@Inject(userServiceToken) public userService: UserService,
		@Inject(masqueradeToken) private masquerade: Masquerade,
		@Inject(goModalToken) private ng1GoModal: ReturnType<typeof goModal>,
		@Inject(groupToken) private Group: ReturnType<typeof GroupFactory>,
		@Inject(userToken) private User: ReturnType<typeof UserFactory>,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(groupSettingsPanelServiceToken) private groupSettingsPanel: GroupSettingsPanelService,
		private dialog: MatDialog,
		private groupService: NgxGroupService,
		private translate: TranslateService,
		private ngxUserService: NgxUserService,
		private ngxGoModalService: GoModalService,
		private routerService: RouterService
	) {}

	public ngOnInit (): void {
		// manage users panel passes EditUser with fullname and role, user search dialog passes User with full_name
		this.fullname = (this.data.user as EditUser).fullname || (this.data.user as User).full_name;
		this.role = (this.data.user as EditUser).role;
		// get all roles excluding the owner role
		if (this.data.group) {
			this.roles = this.Group.roles.filter((role) => {
				return role !== this.Group.role.OWNER
					&& this.Group.rolePrecedence[role] >= this.Group.rolePrecedence[this.data.group.role];
			}).map((role) => {
				// These keys are being used dynamically here. This comment helps the un-used translations check
				// modal-invite-users_controller-invite-confirm-role-presenter
				// modal-invite-users_controller-invite-confirm-role-reviewer
				// modal-invite-users_controller-invite-confirm-role-instructor
				// modal-invite-users_controller-invite-confirm-role-admin
				return {
					role,
					roleKey: `modal-invite-users_controller-invite-confirm-role-${role}`
				};
			});
		}

		this.groups$ = this.groupService.getByUser(this.data.user.user_id, true, false).pipe(
			map((groups) => {
				groups.sort((a, b) => {
					const aDate = typeof a.start_date === 'string' ? a.start_date : a.start_date?.toISOString?.() ?? '';
					const bDate = typeof b.start_date === 'string' ? b.start_date : b.start_date?.toISOString?.() ?? '';
					return aDate.localeCompare(bDate);
				});
				return groups.map((group) => {
					return this.Group.model(group);
				});
			})
		);

		if (this.userService.currentUser.is_root_user) {
			this.credits$ = this.ngxUserService.getBillingBalances(this.data.user.user_id).pipe(
				map((balance) => balance.credits)
			);

			this.recentLogins$ = this.ngxUserService.getRecentLoginHistory(this.data.user.user_id);

			this.linkedUsers$ = this.ngxUserService.getLinkedUsers(this.data.user.user_id).pipe(
				map((linkedUsers) => linkedUsers)
			);

			this.ssoProviders$ = this.ngxUserService.getUser(this.data.user.user_id).pipe(
				map((user) => {
					return user.auth_aliases.filter((alias) => {
						return alias.auth_provider.is_web_app && alias.auth_provider.auth_type === 'oauth';
					}).map((alias) => alias.auth_provider.provider_name).join(', ');
				})
			);
		}
	};

	public showHasPaid (group): boolean {
		return group.requiresPayment() &&
			group.hasPaid() &&
			group.hasPresenterRole();
	};

	public showHasNotPaid (group): boolean {
		return group.requiresPayment() &&
			!group.hasPaid() &&
			group.hasPresenterRole();
	};

	public editUser (user): void {
		this.ngxGoModalService.open(EditUserDialogComponent, false, {
			data: {
				user
			}
		}).afterClosed().subscribe(({shouldClose}) => {
			if (shouldClose) {
				this.dialogRef.close({shouldClose});
			}
		});
	};

	public updateUserRole (user, group, role): void {
		if (!user || !group) {
			return;
		}

		this.groupService.updateUserRole(user.user_id, group.group_id, role)
			.subscribe({
				next: (newRole) => {
					this.role = newRole.role;
				},
				error: () => {
					this.role = (this.data.user as EditUser).role;
				}
			});
	};

	public activityHistory (): void {
		this.ng1GoModal.open({
			modal: 'userActivityHistory',
			modalData: {user: this.data.user}
		}).result.then(({shouldClose}) => {
			if (shouldClose) {
				this.dialogRef.close({shouldClose});
			}
		}).catch(() => {});
	};

	public maskAs (user): void {
		this.masquerade.as(user).catch(() => {
			this.dialog.open(MessageDialogComponent, {
				data: {
					title: this.translate.instant('mask-user-error_title'),
					message: this.translate.instant('mask-user-error_message')
				}
			});
		});
	};

	public isMasked (): boolean {
		return this.masquerade.isMasked();
	};

	public parseUa (ua: string): IResult {
		const parser = new UAParser();
		parser.setUA(ua);
		return parser.getResult();
	}

	public close (group?: Group): void {
		if (!group) {
			this.dialogRef.close({role: this.role});
			return;
		}

		this.dialogRef.close({shouldClose: true});
		if (group.type === 'course') {
			this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDER_VIEW, { folder_id: group.group_id });
			return;
		}

		if (group.type === 'account') {
			this.selectedService.setAccount(group);
			this.groupSettingsPanel.open(group, this.userService.currentUser, 'defaults', {});
			return;
		}

		// resource is org
		this.groupSettingsPanel.open(group, this.userService.currentUser, '', {});
	};
}
